import { useCallback, useEffect, useRef } from "react";
import { Layout, theme, Divider, Menu } from "antd";
import hotkeys from "hotkeys-js";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  SwapOutlined,
  LogoutOutlined,
  FastForwardOutlined,
} from "@ant-design/icons";

import { W0, SIDEBAR_WIDTH } from "@components/Constants";
import { HotKey } from "@components/Text";
import { OrganizationsModal } from "@components/Organization";
import { GoToInvestmentModal } from "@components/Investment";

import { type MenuItem } from "./helpers/menuItems";
import { SidebarDropdown } from "../../../backstage/index.js";

const { Sider } = Layout;

const LOGOUT_PATH = "/logout/backstage";

const LABEL_SIGNOUT = "Sign Out";
const LABEL_SWITCH_TO = "Switch To";
const LABEL_SWITCH_TO_HOTKEY = "ctrl + o";
const LABEL_GO_TO_INVESTMENT = "Go To Investment";
const LABEL_GO_TO_INVESTMENT_HOTKEY = "ctrl + j";

const HOTKEY_SWITCH_TO = "ctrl+o";
const HOTKEY_GO_TO_INVESTMENT = "ctrl+j";

type ModalControls = {
  open?: () => void;
}

type Props = {
  items: MenuItem[];
}

const Sidebar = ({ items }: Props) => {
  const organizationsModal = useRef<ModalControls>({});
  const goToInvestmentModal = useRef<ModalControls>({});

  const { token } = theme.useToken();

  const openOrganizationsModal = useCallback(() => {
    organizationsModal.current.open?.();
  }, []);

  const openGoToInvestmentModal = useCallback(() => {
    goToInvestmentModal.current.open?.();
  }, []);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const dropdownItems: MenuItem[] = [
    {
      key: "investment",
      icon: <FastForwardOutlined />,
      label: (
        <HotKey hotKey={LABEL_GO_TO_INVESTMENT_HOTKEY}>
          <span>{LABEL_GO_TO_INVESTMENT}</span>
        </HotKey>
      ),
      onClick: openGoToInvestmentModal,
    },
    {
      type: "divider",
    },
    {
      key: "switch",
      icon: <SwapOutlined />,
      label: (
        <HotKey hotKey={LABEL_SWITCH_TO_HOTKEY}>
          <span>{LABEL_SWITCH_TO}</span>
        </HotKey>
      ),
      onClick: openOrganizationsModal,
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: (
        <NavLink to={LOGOUT_PATH}>
          {LABEL_SIGNOUT}
        </NavLink>
      ),
    },
  ];

  useEffect(() => {
    hotkeys(HOTKEY_GO_TO_INVESTMENT, (event) => {
      event.preventDefault();
      openGoToInvestmentModal();
    });

    hotkeys(HOTKEY_SWITCH_TO, (event) => {
      event.preventDefault();
      openOrganizationsModal();
    });

    return () => {
      hotkeys.unbind(HOTKEY_GO_TO_INVESTMENT);
      hotkeys.unbind(HOTKEY_SWITCH_TO);
    };
  }, [ openGoToInvestmentModal, openOrganizationsModal ]);

  const selectedKeys = items
    .map(item => `${item.key}`)
    .filter(key => pathname.startsWith(key));

  const { colorBorderSecondary, colorBgContainer } = token;

  const style = {
    top: 0,
    left: 0,
    bottom: 0,
    overflow: "auto" as const,
    position: "fixed" as const,
    background: colorBgContainer,
    borderRight: `1px solid ${colorBorderSecondary}`,
  };

  return (
    <Sider
      width={SIDEBAR_WIDTH}
      style={style}
    >
      <SidebarDropdown
        items={dropdownItems}
      />

      <Divider style={{ margin: 0 }} />

      <Menu
        style={{ border: "none" }}
        items={items}
        onClick={({ key }) => navigate(key)}
        selectedKeys={selectedKeys}
        inlineIndent={W0 + 4}
      />

      <OrganizationsModal
        modal={organizationsModal}
      />

      <GoToInvestmentModal
        modal={goToInvestmentModal}
      />
    </Sider>
  );
};

export default Sidebar;
